import * as pc from 'playcanvas';
import { createMetalMaterial, createGlowMaterial } from './materials.js';
import { LEVEL_CONFIG } from '../config/levels.js';
import { DIFFICULTY_CONFIG } from '../config/difficulty.js';
import { constrainToArenaBounds } from '../systems/collision.js';
import { getArenaBounds } from './arena.js';

// Create a minion entity
export function createMinion(app, level, difficulty = 'normal') {
    const config = LEVEL_CONFIG[level];
    const diffConfig = DIFFICULTY_CONFIG[difficulty] || DIFFICULTY_CONFIG.normal;

    const minion = new pc.Entity('Minion');

    // Smaller version of boss with same color theme
    minion.addComponent('render', {
        type: 'box',
        material: createMetalMaterial(config.bossColor)
    });

    // Spawn at random edge of arena (scaled for arena size)
    const angle = Math.random() * Math.PI * 2;
    const bounds = getArenaBounds(config.arena);
    const spawnDist = bounds ? Math.min(bounds.radiusX, bounds.radiusZ) * 0.7 : 8;
    minion.setPosition(
        Math.cos(angle) * spawnDist,
        0.8,
        Math.sin(angle) * spawnDist
    );
    minion.setLocalScale(1, 1.2, 1);
    app.root.addChild(minion);

    // Add glowing eyes
    const eyeGlow = createGlowMaterial(config.bossGlowColor, 1.2);

    const leftEye = new pc.Entity('MinionLeftEye');
    leftEye.addComponent('render', { type: 'sphere', material: eyeGlow });
    leftEye.setLocalPosition(-0.2, 0.2, 0.52);
    leftEye.setLocalScale(0.15, 0.08, 0.05);
    minion.addChild(leftEye);

    const rightEye = new pc.Entity('MinionRightEye');
    rightEye.addComponent('render', { type: 'sphere', material: eyeGlow });
    rightEye.setLocalPosition(0.2, 0.2, 0.52);
    rightEye.setLocalScale(0.15, 0.08, 0.05);
    minion.addChild(rightEye);

    // Small horn
    const horn = new pc.Entity('MinionHorn');
    horn.addComponent('render', {
        type: 'cone',
        material: createMetalMaterial(new pc.Color(
            config.bossGlowColor.r * 0.5,
            config.bossGlowColor.g * 0.5,
            config.bossGlowColor.b * 0.5
        ))
    });
    horn.setLocalPosition(0, 0.55, 0.2);
    horn.setLocalScale(0.1, 0.25, 0.1);
    horn.setLocalEulerAngles(-30, 0, 0);
    minion.addChild(horn);

    // Minion properties (scaled by difficulty)
    minion.health = (20 + level * 5) * diffConfig.damageMultiplier;
    minion.maxHealth = minion.health;
    minion.speed = (3 + level * 0.5) * diffConfig.bossSpeedMultiplier;
    minion.damage = Math.round((10 + level * 2) * diffConfig.damageMultiplier);
    minion.attackCooldown = 0;
    minion.isAttacking = false;
    minion.stunTimer = 0;
    minion.level = level;

    console.log(`Minion spawned for level ${level}`);
    return minion;
}

// Update minion AI and movement
export function updateMinion(minion, playerPos, collisionObjects, dt) {
    if (minion.health <= 0) return false; // Minion is dead

    const minionPos = minion.getPosition();
    const dx = playerPos.x - minionPos.x;
    const dz = playerPos.z - minionPos.z;
    const distance = Math.sqrt(dx * dx + dz * dz);

    // Stun timer
    if (minion.stunTimer > 0) {
        minion.stunTimer -= dt;
        minion.setEulerAngles(
            Math.sin(minion.stunTimer * 25) * 15,
            minion.getEulerAngles().y,
            Math.cos(minion.stunTimer * 25) * 15
        );
        return true;
    }

    minion.setEulerAngles(0, minion.getEulerAngles().y, 0);

    // Chase player
    if (distance > 1.5) {
        const moveX = (dx / distance) * minion.speed * dt;
        const moveZ = (dz / distance) * minion.speed * dt;

        let newX = minionPos.x + moveX;
        let newZ = minionPos.z + moveZ;

        // Collision with pillars and platforms
        for (const obj of collisionObjects) {
            const odx = newX - obj.x;
            const odz = newZ - obj.z;
            const odist = Math.sqrt(odx * odx + odz * odz);
            const minDist = obj.radius + 0.6;

            if (odist < minDist && odist > 0) {
                newX = obj.x + (odx / odist) * minDist;
                newZ = obj.z + (odz / odist) * minDist;
            }
        }

        // Stay in arena (dynamic bounds based on arena shape)
        const constrained = constrainToArenaBounds(newX, newZ);
        minion.setPosition(constrained.x, minionPos.y, constrained.z);
    }

    // Face player
    const faceAngle = Math.atan2(dx, dz);
    minion.setEulerAngles(0, faceAngle * (180 / Math.PI), 0);

    // Cooldown
    if (minion.attackCooldown > 0) {
        minion.attackCooldown -= dt;
    }

    return true;
}

// Check if minion can attack player
export function canMinionAttack(minion, playerPos) {
    const minionPos = minion.getPosition();
    const dx = playerPos.x - minionPos.x;
    const dz = playerPos.z - minionPos.z;
    const distance = Math.sqrt(dx * dx + dz * dz);

    return distance < 1.5 && minion.attackCooldown <= 0;
}

// Trigger minion attack animation
export function minionAttack(minion) {
    minion.attackCooldown = 1.5;
    minion.isAttacking = true;

    // Attack animation
    minion.setLocalScale(1.3, 0.9, 1.3);
    setTimeout(() => {
        if (minion && !minion.destroyed) {
            minion.setLocalScale(1, 1.2, 1);
            minion.isAttacking = false;
        }
    }, 150);
}

// Clear all minions
export function clearMinions(minions) {
    minions.forEach(m => {
        if (m && !m.destroyed) m.destroy();
    });
    return [];
}

// Get max minion count for level
export function getMaxMinions(level, difficulty = 'normal') {
    const diffConfig = DIFFICULTY_CONFIG[difficulty] || DIFFICULTY_CONFIG.normal;
    const baseMax = level - 1; // Level 3: 2, Level 4: 3, Level 5: 4
    return Math.ceil(baseMax * diffConfig.minionSpawnMultiplier);
}

// Get minion spawn interval for level
export function getSpawnInterval(level) {
    return 8 - level; // Faster spawns at higher levels
}
